import React, { useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useSelector } from 'react-redux';
import { FeaturesReady } from '@growthbook/growthbook-react';

import { PhotographPropTypes } from 'dpl/shared/utils/customPropTypes';
import { isLessThanBreakpoint } from 'dpl/util/grid';
import SmartImage from 'dpl/components/SmartImage';
import TooltipWrapper from 'dpl/common/tooltip/components/TooltipWrapper';
import Icon from 'dpl/common/components/Icon';
import useCurrentUser from 'dpl/common/hooks/useCurrentUser';
import { FOLLOWABLE_TYPES } from 'dpl/favorites/utils/constants';
import FavoriteButton from 'dpl/favorites/components/FavoriteButton';
import { sendRudderstackEvent } from 'dpl/util/analytics';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_VIEW_FEATURES
} from 'dpl/constants/analytics';
import PreferredBreederBadge from 'dpl/breeder_preferred_qualifications/components/PreferredBreederBadge';
import {
  CARD_BADGE_HEIGHT,
  CARD_BADGE_WIDTH,
  STATUS
} from 'dpl/breeder_preferred_qualifications/utils/constants';
import { RESULTS_TYPES } from 'dpl/puppy_search_results/utils/constants';

import CardFooter from './CardFooter';
import CardMediaCarousel from './CardMediaCarousel';

const isLessThanLg = isLessThanBreakpoint('lg');

export default function Card({
  profile,
  lastUpdated,
  analytics,
  onFavoriteChange,
  trackInView
}) {
  const stateLander = useSelector(({ server }) => server.stateLander);
  const locationTitle = useSelector(({ server }) => server.locationTitle);
  const citySlug = useSelector(({ server }) => server.citySlug);

  const {
    cropped_photo_url: croppedPhotoUrl,
    delivery_methods: deliveryMethods,
    delivery_methods_count: deliveryMethodsCount,
    name,
    description,
    distance_in_miles: distance,
    availability_description: availabilityDescription,
    available_puppy_count: availablePuppyCount,
    availability_info: availabilityInfo,
    display_apply_cta: displayApplyCta,
    profile_photo_url: profilePhotoUrl,
    snoozed,
    url,
    missing_photo_url: missingPhotoUrl,
    result_photos: resultPhotos,
    breeder_breed_id: breederBreedId,
    breed_id: breedId,
    id: breederProfileId,
    testimonials_count: testimonialsCount,
    first_name: breederFirstName,
    preferred_qualification: preferredQualification
  } = profile;

  let { facility_location: facilityLocation } = profile;

  if (stateLander) {
    facilityLocation = profile.facility_location_full_state;
  }

  const photosWithAlt = useMemo(
    () =>
      resultPhotos.map(photo => ({
        ...photo,
        alt: locationTitle ? `${name} in ${locationTitle}` : name
      })),
    [locationTitle, lastUpdated]
  );

  const displayPhotoUrl = croppedPhotoUrl || profilePhotoUrl;
  const mileOrMiles = distance === 1 ? 'mile' : 'miles';

  const milesAway = distance ? `\u2022 ${distance} ${mileOrMiles} away` : '';
  const milesFrom = distance ? `${distance} ${mileOrMiles} away from` : '';

  const smartImageProps = {
    Tag: 'div',
    className: 'bg-center cover br2',
    crop: true,
    loadingClass: 'bg-light-gray',
    height: isLessThanLg ? '150px' : '180px',
    width: isLessThanLg ? '150px' : '180px',
    allowContextMenu: true
  };

  function displayBreederLocation() {
    if (citySlug && milesFrom) return `${milesFrom} ${locationTitle}`;
    if (facilityLocation) return `${facilityLocation} ${milesAway}`;
    return <>&nbsp;</>;
  }

  const currentUser = useCurrentUser();
  const { isBuyer, isLoggedIn } = currentUser;

  const favoriteAnalytics = {
    ...analytics,
    breed_id: breedId,
    breeder_name: name,
    breeder_preferred_status: preferredQualification?.status,
    breeder_profile_id: breederProfileId,
    page_number: analytics.page_number || null,
    position: analytics.position || null,
    puppies_available: availablePuppyCount,
    transportation: Object.keys(deliveryMethods || {}).reduce(
      (allTypes, key) => {
        return [...allTypes, ...deliveryMethods[key].map(dm => dm.name)];
      },
      []
    )
  };

  const showFavoriteButton = !isLoggedIn || isBuyer;

  const verifiedStoriesText =
    testimonialsCount > 1 ? 'verified stories' : 'verified story';

  const computedAnalytics = {
    ...analytics,
    breeder_preferred_status: preferredQualification?.status,
    breeder_profile_id: breederProfileId,
    view_feature: ANALYTICS_VIEW_FEATURES.BREEDER_CARD
  };

  function handleClick() {
    sendRudderstackEvent(ANALYTICS_EVENTS.CARD_CLICKED_BY_USER, {
      ...computedAnalytics,
      breed_id: breedId,
      miles_away: distance,
      transportation: favoriteAnalytics.transportation
    });
  }

  const isPreferredBreeder =
    preferredQualification?.status === STATUS.PREFERRED;

  const [ref] = useInView({
    triggerOnce: true,
    threshold: 0.8,
    onChange: inView => {
      if (inView) trackInView();
    }
  });

  return (
    <a
      ref={ref}
      className="BreederSearchResultsCard h-100 db"
      href={url}
      onClick={handleClick}
    >
      <div className="BreederSearchResultsCard__container h-100 flex flex-column justify-between br3 shadow-2 ba b--light-gray">
        <div>
          <div className="BreederSearchResultsCard__info flex items-center ph4 pt4 mb2 justify-between">
            <div className="flex mr2">
              <SmartImage
                Tag="div"
                width="40px"
                height="40px"
                lazy
                alt={name}
                className="bg-top bg-center-l bg-center-m cover h-100 w-100 br-100"
                loadingClass="bg-light-gray"
                src={displayPhotoUrl}
              />
            </div>
            <div className="w-100 overflow-auto">
              <div className="flex items-center">
                <p className="BreederSearchResultsItem__name fw-medium f2 f3-md truncate">
                  {name}
                </p>
                {isPreferredBreeder && (
                  <PreferredBreederBadge
                    className="mh2 flex"
                    height={CARD_BADGE_HEIGHT}
                    width={CARD_BADGE_WIDTH}
                  />
                )}
              </div>
              <p className="f1 mid-gray truncate">{displayBreederLocation()}</p>
            </div>
            {showFavoriteButton && (
              <FavoriteButton
                analytics={favoriteAnalytics}
                followableTypeId={breederBreedId}
                followableType={FOLLOWABLE_TYPES.BREEDER_BREED}
                onChange={onFavoriteChange}
                iconSize="20px"
              />
            )}
          </div>
          {!snoozed && (
            <div className="flex mt4 overflow-hidden">
              <div className="BreederSearchResultsItem__image w-100 h-100 br3 relative z-0">
                <FeaturesReady timeout={500}>
                  <CardMediaCarousel
                    mediaFiles={
                      photosWithAlt.length === 0
                        ? [{ url: missingPhotoUrl }]
                        : photosWithAlt
                    }
                    smartImageProps={smartImageProps}
                    showCaptions
                    analytics={{
                      ...computedAnalytics,
                      breeder_preferred_status: undefined,
                      breeder_profile_id: undefined
                    }}
                  />
                </FeaturesReady>
              </div>
            </div>
          )}
          <div className="mh4 mt4 pb4 f2">
            <div className="flex justify-between mb2 items-center">
              <div
                className={classnames({
                  'bg-light-gray dark-gray w-100 tc pv2 br3': snoozed,
                  'fw-medium': !snoozed
                })}
              >
                {availabilityInfo ? (
                  <TooltipWrapper
                    data-test-id="search-rank-transportation-tooltip"
                    title={availabilityInfo}
                    minWidth="250px"
                  >
                    <p className="flex align-items-center">
                      {availabilityDescription}
                      <Icon
                        name="help-16"
                        width="12"
                        height="12"
                        className="ml1 mid-gray"
                      />
                    </p>
                  </TooltipWrapper>
                ) : (
                  <p>{availabilityDescription}</p>
                )}
              </div>
              {testimonialsCount > 0 && (
                <span className="inline-flex items-center pv1 ph2 br3 bg-yellow-10">
                  <Icon
                    className="mr1 gold"
                    name="star-filled-16"
                    width="14px"
                    height="14px"
                  />
                  {testimonialsCount} {verifiedStoriesText}
                </span>
              )}
            </div>
            <p className="mid-gray">{description}</p>
          </div>
        </div>
        {!snoozed && (
          <CardFooter
            snoozed={snoozed}
            deliveryMethods={deliveryMethods}
            breederProfileUrl={url}
            displayApplyCta={displayApplyCta}
            breederProfileId={breederProfileId}
            analytics={computedAnalytics}
            breederFirstName={breederFirstName}
            facilityLocation={facilityLocation}
            deliveryMethodsCount={deliveryMethodsCount}
            cardType={RESULTS_TYPES.BREEDERS}
          />
        )}
      </div>
    </a>
  );
}

Card.propTypes = {
  profile: PropTypes.shape({
    cropped_photo_url: PropTypes.string,
    availability_description: PropTypes.string,
    availability_info: PropTypes.string,
    display_apply_cta: PropTypes.bool,
    facility_location_full_state: PropTypes.string,
    // eslint-disable-next-line react/forbid-prop-types
    delivery_methods: PropTypes.object,
    description: PropTypes.string,
    snoozed: PropTypes.bool,
    name: PropTypes.string,
    facility_location: PropTypes.string,
    distance_in_miles: PropTypes.number,
    profile_photo_url: PropTypes.string,
    result_photos: PropTypes.arrayOf(PropTypes.shape(PhotographPropTypes))
      .isRequired,
    url: PropTypes.string.isRequired,
    missing_photo_url: PropTypes.string,
    available_puppy_count: PropTypes.number,
    breeder_breed_id: PropTypes.number,
    id: PropTypes.number,
    breed_id: PropTypes.number,
    testimonials_count: PropTypes.number,
    first_name: PropTypes.string,
    delivery_methods_count: PropTypes.number,
    preferred_qualification: PropTypes.shape({
      status: PropTypes.string
    })
  }).isRequired,
  analytics: PropTypes.shape({
    page_number: PropTypes.number,
    position: PropTypes.number,
    view: PropTypes.string
  }).isRequired,
  lastUpdated: PropTypes.number,
  onFavoriteChange: PropTypes.func,
  trackInView: PropTypes.func
};

Card.defaultProps = {
  lastUpdated: null,
  onFavoriteChange: null,
  trackInView: () => {}
};
